// @flow
import React, {FunctionComponent} from 'react';
import {useHistory, useLocation, useParams} from "react-router-dom";
import FlexStack from "../components/Common/flex-stack";
import TreatmentType from "../api/dtos/treatment-type";
import MedicationForm from "../components/AnimalDashboard/TreatmentDialog/medication-form";
import SimpleNumberValueForm from "../components/AnimalDashboard/TreatmentDialog/simple-number-value-form";
import qs from "qs";
import {TreatmentListJson} from "../api/generated/medical-rest";
import {DateTime} from "luxon";
import TreatmentService from "../services/treatment-service";
import RouteService from "../services/route-service";
import SimpleNoteForm from "../components/AnimalDashboard/TreatmentDialog/simple-note-form";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {Widget} from "../components/Common";
import {AnimalService} from "../services/animal-service";
import {useAnimal} from "../components/Common/hooks/use-animal";

type TreatmentEditorPathParams = {
    animalId: string;
};

export type TreatmentEditorLocationState = {
    treatment?: TreatmentListJson;
    backLocation?: string;
};

const TreatmentEditor: FunctionComponent = () => {
    const history = useHistory();
    const {animalId} = useParams<TreatmentEditorPathParams>();
    const {state, search} = useLocation<TreatmentEditorLocationState>();
    const {t} = useTranslation();

    const parsedQueryString = qs.parse(search, {ignoreQueryPrefix: true});
    const treatmentType = String(parsedQueryString.treatmentType);
    const panonId = String(parsedQueryString.panonId);

    const queryClient = useQueryClient();
    const {animal} = useAnimal(panonId);
    const {mutateAsync: saveTreatment, isPending} = useMutation({
        mutationFn: async (treatment: TreatmentListJson) => treatment.id
            ? await TreatmentService.updateTreatment(treatment)
            : await TreatmentService.addTreatment(treatment),
        onSettled: async () => await Promise.all([
            queryClient.invalidateQueries({queryKey: ['treatment']}),
            queryClient.invalidateQueries({queryKey: ['treatments']}),
            queryClient.invalidateQueries({queryKey: ['pregnancy']}),
            queryClient.invalidateQueries({queryKey: ['pregnancies']}),
        ]),
    });

    const treatmentTemplate: TreatmentListJson = state?.treatment ?? {
        animalId,
        dose: 0,
        drug: '',
        id: '',
        note: '',
        timestamp: DateTime.now(),
        type: '',
        unit: '',
        value: 0,
        private: false,
        isPrivate: false,
    };
    treatmentTemplate.isPrivate = treatmentTemplate.private; // fix private property for writing

    const navigateBack = () => history.push(state?.backLocation
        ? state?.backLocation
        : RouteService.expand(RouteService.ANIMAL_DETAILS, {panonId}));
    const handleTreatmentFormCancel = navigateBack;

    const handleTreatmentSave = async (treatment: TreatmentListJson) => {
        await saveTreatment(treatment);
        navigateBack();
    };

    const renderFormForType = () => {
        switch (treatmentType) {
            case TreatmentType.vitaminsAndMinerals:
            case TreatmentType.otherMedication:
            case TreatmentType.vaccination:
            case TreatmentType.antiparasitics:
                return <MedicationForm prefill={{...treatmentTemplate, type: treatmentType, unit: "ml"}}
                                       isPending={isPending}
                                       onFormSave={handleTreatmentSave}
                                       onFormCancel={handleTreatmentFormCancel}/>;
            case TreatmentType.bodyscore:
                return <SimpleNumberValueForm prefill={{...treatmentTemplate, type: treatmentType}}
                                              valueFormHelperText="Bodyscore"
                                              isPending={isPending}
                                              onFormSave={handleTreatmentSave}
                                              onFormCancel={handleTreatmentFormCancel}/>;
            case TreatmentType.weighing:
                return <SimpleNumberValueForm prefill={{...treatmentTemplate, type: treatmentType, unit: "kg"}}
                                              valueFormHelperText="Gewicht"
                                              isPending={isPending}
                                              onFormSave={handleTreatmentSave}
                                              onFormCancel={handleTreatmentFormCancel}/>;
            case TreatmentType.ultrasound:
            case TreatmentType.spittest:
            case TreatmentType.other:
                return <SimpleNoteForm prefill={{...treatmentTemplate, type: treatmentType}}
                                       isPending={isPending}
                                       onFormSave={handleTreatmentSave}
                                       onFormCancel={handleTreatmentFormCancel}/>
            default:
                return <MedicationForm prefill={{...treatmentTemplate, type: TreatmentType.otherMedication}}
                                       isPending={isPending}
                                       onFormSave={handleTreatmentSave}
                                       onFormCancel={handleTreatmentFormCancel}/>;
        }
    };

    const title = t(treatmentType);
    const animalName = AnimalService.getAnimalFullName(animal);
    const displayName = title === treatmentType ? 'Sonstige Behandlung' : title;

    return (
        <FlexStack>
            <Widget displayName={`${displayName}${animalName ? ` für ${animalName}` : ''}`}>
                <FlexStack>
                    {renderFormForType()}
                </FlexStack>
            </Widget>
        </FlexStack>
    );
};

export default TreatmentEditor
